import type { ComponentChildren } from 'preact';
import type { Contribution } from '../api/types';
import type { PhasePromptGroup } from '../lib/phasePrompts';
import styles from './PhaseSection.module.css';

interface PhaseSectionProps {
  phase: string;
  contributionCount: number;
  authors: Map<string, string>; // authorId -> displayName
  /**
   * The prompts the takes in this phase answered, shown under the header.
   * Grouped because a role-aware phase asks each role something different —
   * see `resolvePhasePromptGroups`.
   */
  promptGroups?: PhasePromptGroup[];
  isCollapsed: boolean;
  onToggle: () => void;
  sectionRef?: (element: HTMLDivElement | null) => void;
  children: ComponentChildren;
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  'var(--st-author-1)', 'var(--st-author-2)', 'var(--st-author-3)',
  'var(--st-author-4)', 'var(--st-author-5)', 'var(--st-author-6)',
  'var(--st-author-7)', 'var(--st-author-8)', 'var(--st-author-9)',
  'var(--st-author-10)',
];

function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function PhaseSection({
  phase,
  contributionCount,
  authors,
  promptGroups,
  isCollapsed,
  onToggle,
  sectionRef,
  children,
}: PhaseSectionProps) {
  const authorEntries = Array.from(authors.entries()).slice(0, 4);
  const authorCount = authors.size;
  const extraAuthors = authorCount - authorEntries.length;

  return (
    <div class={styles.section} ref={sectionRef} data-phase={phase}>
      <button
        type="button"
        class={styles.header}
        onClick={onToggle}
        aria-expanded={!isCollapsed}
      >
        <div class={styles.headerLeft}>
          <span class={styles.phaseAccent} />
          <span class={styles.phaseName}>{phase}</span>
          <span class={styles.stats}>
            {contributionCount} {contributionCount === 1 ? 'take' : 'takes'} from {authorCount} {authorCount === 1 ? 'person' : 'people'}
          </span>
        </div>

        <div class={styles.headerRight}>
          <div class={styles.avatarStack}>
            {authorEntries.map(([id, name]) => (
              <div
                key={id}
                class={styles.stackedAvatar}
                style={{ background: avatarColor(id) }}
                title={name}
              >
                {getInitials(name)}
              </div>
            ))}
            {extraAuthors > 0 && (
              <div class={styles.stackedAvatarExtra}>+{extraAuthors}</div>
            )}
          </div>
          <span class={`${styles.chevron} ${isCollapsed ? '' : styles.chevronOpen}`}>&#9660;</span>
        </div>
      </button>

      {!isCollapsed && promptGroups && promptGroups.length > 0 && (
        <div class={styles.prompts}>
          {promptGroups.map((group, groupIndex) => (
            <div key={group.label ?? groupIndex} class={styles.promptGroup}>
              {group.label && <p class={styles.promptRole}>{group.label}</p>}
              {group.prompts.map((prompt, index) => (
                <p key={index} class={styles.promptLine}>{prompt}</p>
              ))}
            </div>
          ))}
        </div>
      )}

      {!isCollapsed && (
        <div class={styles.body}>
          {children}
        </div>
      )}
    </div>
  );
}
